// 하루 운동 순서 그림(자체 SVG, 2026-10-20). 번호 순서의 운동을 고리 모양으로 그린다 — 가운데는 등급 이름.
// 서버(사용자 페이지 sharePage.js)와 센터 화면(FrailGuideBlock)이 같은 함수를 쓴다. 의존성 없음, 글은 전부 이스케이프.
// ⚠️ CommonJS(module.exports)인 채로 React 번들에도 들어간다. CRA 의 babel 이 ESM `import` 헬퍼를 끼워 넣는 문법(배열·인자 펼치기 `...`,
//    나머지 매개변수, class 등)을 쓰면 웹팩이 이 파일을 ESM 으로 보고 module.exports 대입에서 런타임 오류가 난다(2026-10-20 dev 장애).
//    sharePage.test.js 가 CRA 프리셋으로 변환해 import 가 안 생기는지 고정한다.
// 외부 프로그램의 그림을 흉내 내지 않는다: 우리 운동 이름·분류 색·등급만 담는다.

const CATEGORY_COLOR = { WARMUP: '#9ad0c2', STRENGTH: '#f3b6a0', BALANCE: '#a7c7f2', AEROBIC: '#b8e0a6', FLEXIBILITY: '#d9c2ef' };
const CATEGORY_NAME = { WARMUP: '준비', STRENGTH: '근력', BALANCE: '균형', AEROBIC: '걷기', FLEXIBILITY: '유연' };

const esc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
// 배열 구조 분해(const [x, y] = …)도 babel 헬퍼(slicedToArray)를 끼워 넣으므로 객체로 돌려준다.
const polar = (cx, cy, r, deg) => { const a = ((deg - 90) * Math.PI) / 180; return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) }; };
const f = (n) => n.toFixed(1);

/** 고리 조각 path(안쪽 r1, 바깥 r2, 각 a0→a1). 조각 사이 틈은 호출자가 각도로 준다. */
const ringSegment = (cx, cy, r1, r2, a0, a1) => {
    const p0 = polar(cx, cy, r2, a0);
    const p1 = polar(cx, cy, r2, a1);
    const p2 = polar(cx, cy, r1, a1);
    const p3 = polar(cx, cy, r1, a0);
    const large = a1 - a0 > 180 ? 1 : 0;
    return `M${f(p0.x)} ${f(p0.y)} A${f(r2)} ${f(r2)} 0 ${large} 1 ${f(p1.x)} ${f(p1.y)} L${f(p2.x)} ${f(p2.y)} A${f(r1)} ${f(r1)} 0 ${large} 0 ${f(p3.x)} ${f(p3.y)} Z`;
};

/** 이름을 고리 조각 안에 들어가게 낱말(공백·가운뎃점) 단위로 최대 세 줄까지 나눈다. 넘치면 마지막 줄을 줄임표로. */
const splitName = (name, max = 7, maxLines = 3) => {
    const text = String(name || '').trim();
    if (text.length <= max) return [text];
    const tokens = text.match(/[^ ·]+[ ·]?/g) || [text];
    const lines = [];
    let cur = '';
    for (const tok of tokens) {
        if (!cur || (cur + tok).trim().length <= max) cur += tok;
        else { lines.push(cur.trim()); cur = tok; }
    }
    if (cur.trim()) lines.push(cur.trim());
    if (lines.length > maxLines) {
        const kept = lines.slice(0, maxLines);
        kept[maxLines - 1] = `${kept[maxLines - 1].slice(0, max - 1)}…`;
        return kept;
    }
    return lines.map((l) => (l.length > max + 1 ? `${l.slice(0, max)}…` : l));
};

/**
 * @param {object} p
 * @param {Array<{no:number,name:string,category?:string}>} p.items  번호 순서의 운동
 * @param {string} [p.title]     가운데 큰 글(등급 이름)
 * @param {string} [p.subtitle]  가운데 작은 글
 * @param {number} [p.size]      한 변(px). 기본 360
 * @returns {string} <svg …>
 */
const renderWheelSvg = ({ items = [], title = '', subtitle = '', size = 360 } = {}) => {
    const n = items.length;
    const cx = size / 2;
    const cy = size / 2;
    const legendBand = size * 0.1;   // 범례는 고리 아래 띠에 — 고리와 겹치지 않게(2026-10-21)
    const height = size + legendBand;
    const r2 = size * 0.44;   // 번호 배지가 고리 바깥에 놓일 자리를 남긴다(2026-10-21: 이름과 겹치지 않게)
    const r1 = size * 0.26;
    const gap = n > 1 ? 2.2 : 0;
    const step = n > 0 ? 360 / n : 360;
    const segments = items.map((it, i) => {
        const a0 = i * step + gap / 2;
        const a1 = (i + 1) * step - gap / 2;
        const mid = (a0 + a1) / 2;
        // 번호는 고리 바깥, 이름은 조각 가운데 — 둘이 겹치지 않게
        const t = polar(cx, cy, (r1 + r2) / 2, mid);
        const n = polar(cx, cy, r2 + size * 0.038, mid);
        const tx = t.x; const ty = t.y; const nx = n.x; const ny = n.y;
        const color = CATEGORY_COLOR[it.category] || '#e2e8f0';
        const lines = splitName(it.name);
        const fs = size * 0.031;
        const lh = fs * 1.15;
        // 여러 줄이면 블록을 세로 가운데에: 첫 줄을 (줄 수-1)/2 줄만큼 올린다
        const text = lines.map((line, li) => `<tspan x="${f(tx)}" dy="${f(li === 0 ? fs * 0.35 - ((lines.length - 1) / 2) * lh : lh)}">${esc(line)}</tspan>`).join('');
        return `<g class="seg">
    <path d="${ringSegment(cx, cy, r1, r2, a0, a1)}" fill="${color}" stroke="#fff" stroke-width="1.5"/>
    <circle cx="${f(nx)}" cy="${f(ny)}" r="${f(size * 0.028)}" fill="#1f2937"/>
    <text x="${f(nx)}" y="${f(ny)}" dy="${f(size * 0.011)}" text-anchor="middle" font-size="${f(size * 0.032)}" font-weight="700" fill="#fff">${esc(it.no)}</text>
    <text x="${f(tx)}" y="${f(ty)}" text-anchor="middle" font-size="${f(fs)}" fill="#1f2937">${text}</text>
  </g>`;
    }).join('\n');
    const legend = Array.from(new Set(items.map((it) => it.category).filter((c) => CATEGORY_COLOR[c])))
        .map((c, i) => `<g transform="translate(${f(size * 0.06 + i * size * 0.19)} ${f(size + legendBand * 0.4)})"><rect width="${f(size * 0.03)}" height="${f(size * 0.03)}" rx="3" fill="${CATEGORY_COLOR[c]}"/><text x="${f(size * 0.04)}" y="${f(size * 0.026)}" font-size="${f(size * 0.03)}" fill="#4a5568">${esc(CATEGORY_NAME[c])}</text></g>`)
        .join('');
    const centerTitle = title ? `<text x="${f(cx)}" y="${f(cy - size * 0.01)}" text-anchor="middle" font-size="${f(size * 0.05)}" font-weight="700" fill="#1f2937">${esc(title)}</text>` : '';
    const centerSub = subtitle ? `<text x="${f(cx)}" y="${f(cy + size * 0.05)}" text-anchor="middle" font-size="${f(size * 0.032)}" fill="#6b7280">${esc(subtitle)}</text>` : '';
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${f(height)}" width="${size}" height="${f(height)}" role="img" aria-label="하루 운동 순서${title ? ` · ${esc(title)}` : ''}" font-family="-apple-system, BlinkMacSystemFont, 'Apple SD Gothic Neo', 'Malgun Gothic', 'Noto Sans KR', sans-serif">
  <circle cx="${f(cx)}" cy="${f(cy)}" r="${f(r1 - 4)}" fill="#fff" stroke="#e5e7eb"/>
${segments}
  ${centerTitle}
  ${centerSub}
  ${legend}
</svg>`;
};

module.exports = { renderWheelSvg, CATEGORY_COLOR, CATEGORY_NAME, splitName };
